/**
 * useSurebetViewFilters - client-side view-filter state for Arbitrage.
 *
 * Independent from FilterContext (API bookmaker recalculation).
 * Persists under localStorage key `arbiwin_view_filters`.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DEFAULT_SUREBET_VIEW_FILTERS,
  filterSurebetsView,
  hasActiveSurebetViewFilters
} from './surebetViewFilters';
import {
  loadSurebetViewFilters,
  saveSurebetViewFilters,
  normalizeSurebetViewFilters
} from './surebetViewFilterPersistence';

/**
 * @param {Array} surebets - full surebets list from SurebetContext (API result)
 * @returns {Object} filter state, setters, and filteredSurebets
 */
const useSurebetViewFilters = (surebets = []) => {
  const [filters, setFiltersState] = useState(() => loadSurebetViewFilters());

  useEffect(() => {
    saveSurebetViewFilters(filters);
  }, [filters]);

  const setFilters = useCallback((updater) => {
    setFiltersState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return normalizeSurebetViewFilters(next);
    });
  }, []);

  const setMinMargin = useCallback((minMargin) => {
    setFilters((prev) => ({ ...prev, minMargin }));
  }, [setFilters]);

  const setSearchQuery = useCallback((searchQuery) => {
    setFilters((prev) => ({ ...prev, searchQuery }));
  }, [setFilters]);

  const setSelectedChampionships = useCallback((selectedChampionships) => {
    setFilters((prev) => ({ ...prev, selectedChampionships }));
  }, [setFilters]);

  const setSelectedMarketKeys = useCallback((selectedMarketKeys) => {
    setFilters((prev) => ({ ...prev, selectedMarketKeys }));
  }, [setFilters]);

  const setTimeWindow = useCallback((timeWindow) => {
    setFilters((prev) => ({ ...prev, timeWindow }));
  }, [setFilters]);

  const resetViewFilters = useCallback(() => {
    setFiltersState({ ...DEFAULT_SUREBET_VIEW_FILTERS });
  }, []);

  const filteredSurebets = useMemo(
    () => filterSurebetsView(surebets, filters),
    [surebets, filters]
  );

  const hasActiveViewFilters = useMemo(
    () => hasActiveSurebetViewFilters(filters),
    [filters]
  );

  return {
    viewFilters: filters,
    filteredSurebets,
    filteredCount: filteredSurebets.length,
    hasActiveViewFilters,
    setViewFilters: setFilters,
    setMinMargin,
    setSearchQuery,
    setSelectedChampionships,
    setSelectedMarketKeys,
    setTimeWindow,
    resetViewFilters
  };
};

export default useSurebetViewFilters;
